import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import i18n from '../../../Config/app/i18n';
import errorMapper, { Error, ErrorCodes } from '../../utils/errorMapper';

const DEFAULT_FORM_VALUES = {
  new_password1: '',
  new_password2: '',
  token: '',
  uidb64: '',
  recaptcha_response_v3: '',
};

const ResetPasswordValidator = z
  .object({
    new_password1: z.string().min(1, i18n.t('validation:password_required')),
    new_password2: z.string().min(1, i18n.t('validation:password_required')),
    recaptcha_response_v3: z.string(),
    recaptcha_response_v2: z.string().optional(),
  })
  .refine((data) => data.new_password1 === data.new_password2, {
    message: i18n.t('validation:match_password'),
    path: ['new_password2'],
  });

export type ResetPasswordForm = z.infer<typeof ResetPasswordValidator> & {
  token: string;
  uidb64: string;
};

export const useResetPassword = () => {
  const { track } = useAnalytics();
  const [searchParams] = useSearchParams();
  const [useV2, setUseV2] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(ResetPasswordValidator),
  });

  const mutation = useMutation<{ email: string }, Error<ResetPasswordForm>, ResetPasswordForm>({
    mutationKey: ['accounts/reset-password'],
    onSuccess: () => {
      track('reset_password_form_success', {
        event_category: 'User reset password',
        event_label: 'reset_password_form',
      });
    },
    onError: (error) => {
      track('reset_password_form_error', {
        event_category: 'User reset password',
        event_label: 'reset_password_form',
      });

      errorMapper(error.response?.data?.errors || {}, (field, message, code) => {
        if (field === 'recaptcha_response_v3') {
          setUseV2(true);
        }

        if (code === ErrorCodes.INVALID_TOKEN) {
          setError('token', {
            type: 'manual',
            message: i18n.t('validation:invalid_token'),
          });
        }

        setError(field, { message });
      });
    },
  });

  const onSubmit = (data: ResetPasswordForm) => {
    track('reset_password_submit', {
      event_category: 'User reset password',
      event_label: 'reset_password_form',
    });

    mutation.mutate({
      ...data,
      token: searchParams.get('token')!,
      uidb64: searchParams.get('uidb64')!,
    });
  };

  const onVerify = useCallback(
    (token: string) => {
      setValue('recaptcha_response_v3', token);
    },
    [mutation.submittedAt],
  );

  register('token', { required: true });
  register('uidb64', { required: true });
  register('recaptcha_response_v3', { required: true });

  return {
    control,
    useV2,
    register,
    handleSubmit,
    onSubmit,
    errors,
    onVerify,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
};
